import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ChartModule } from 'primeng/chart';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastrService } from 'ngx-toastr';
import { CustomerReadComponent } from '../customers/customer-read.component';
import { SaleReadComponent } from '../sales/sale-read.component';
import { AuthService } from '../../services/auth.service';
import { CustomerService, type CustomerDTO } from '../../services/customer.service';
import { SaleService, type SaleDTO } from '../../services/sale.service';
import type { ChargeDTO } from '../../core/models';
import {
  ForecastCashFlowV2Service,
  type ForecastCashFlowV2Data,
  type ForecastCustomerCashFlowDetail,
  type ForecastCustomerCompareBucket,
  type ForecastCustomerSaleBucket,
  type ForecastSnapshotCardDetail,
  type ForecastSnapshotCardKind
} from '../../services/forecast-cash-flow-v2.service';
import { browserCalendarMonth, browserCalendarYear } from '../../core/date-utils';
import type { ChartType, Plugin } from 'chart.js';

type BarSaleCountDataset = {
  saleCounts?: number[];
};

type SaleCountHit = {
  datasetIndex: number;
  index: number;
  left: number;
  top: number;
  right: number;
  bottom: number;
};

const saleCountHits = new WeakMap<object, SaleCountHit[]>();

function findSaleCountHit(chart: object | undefined, event: unknown): SaleCountHit | undefined {
  if (!chart) return undefined;
  const x = (event as { x?: number }).x;
  const y = (event as { y?: number }).y;
  if (x == null || y == null) return undefined;
  const hits = saleCountHits.get(chart) ?? [];
  for (let i = hits.length - 1; i >= 0; i--) {
    const h = hits[i];
    if (x >= h.left && x <= h.right && y >= h.top && y <= h.bottom) return h;
  }
  return undefined;
}

/** Dibuja el número de pagos al pie de cada barra de Ingresos de caja. */
const cashflowBarSaleCountPlugin: Plugin<ChartType> = {
  id: 'cashflowBarSaleCounts',
  afterDatasetsDraw(chart) {
    const ctx = chart.ctx;
    const area = chart.chartArea;
    const hits: SaleCountHit[] = [];
    const font = '600 9px ui-sans-serif, system-ui, sans-serif';
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta.visible || meta.type !== 'bar') return;
      const counts = (dataset as BarSaleCountDataset).saleCounts ?? [];
      meta.data.forEach((element, index) => {
        const count = counts[index] ?? 0;
        if (count <= 0) return;
        const x = element.x;
        const y = element.y;
        const base = (element as { base?: number }).base ?? area.bottom;
        if (!Number.isFinite(x) || x < area.left || x > area.right) return;
        const height = Math.abs(base - y);
        const text = String(count);
        const textY = height >= 14 ? base - 3 : Math.min(y, base) - 2;
        ctx.save();
        ctx.font = font;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = height >= 14 ? 'rgba(255,255,255,0.95)' : '#64748b';
        ctx.fillText(text, x, textY);
        const w = Math.max(ctx.measureText(text).width, 10);
        hits.push({
          datasetIndex,
          index,
          left: x - w / 2 - 6,
          right: x + w / 2 + 6,
          top: textY - 12,
          bottom: textY + 6
        });
        ctx.restore();
      });
    });
    saleCountHits.set(chart, hits);
  }
};

@Component({
  selector: 'app-forecast-cash-flow-v2',
  templateUrl: './forecast-cash-flow-v2.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CurrencyPipe,
    DatePipe,
    ChartModule,
    ButtonModule,
    DialogModule,
    DrawerModule,
    TableModule,
    TagModule,
    TooltipModule,
    CustomerReadComponent,
    SaleReadComponent
  ],
  styles: [
    `
      :host ::ng-deep .p-chart {
        display: block;
        width: 100%;
        height: 100%;
        overflow: visible !important;
      }
      :host ::ng-deep .p-chart canvas {
        max-width: 100% !important;
        max-height: 100% !important;
      }
      :host ::ng-deep .cashflow-panel .p-chart,
      :host ::ng-deep .cashflow-panel .p-chart canvas {
        width: 100% !important;
        height: 100% !important;
        max-height: none !important;
      }
      /* Evita que el tooltip del canvas se recorte por el contenedor */
      .chart-panel,
      .chart-panel .chart-area {
        overflow: visible;
      }
      ::ng-deep .forecast-help-tooltip.p-tooltip,
      ::ng-deep .p-tooltip.forecast-help-tooltip {
        max-width: 22rem;
      }
      ::ng-deep .forecast-help-tooltip .p-tooltip-text,
      ::ng-deep .p-tooltip.forecast-help-tooltip .p-tooltip-text {
        white-space: pre-line;
        line-height: 1.45;
        font-size: 0.75rem;
        text-align: left;
      }
      :host ::ng-deep .p-drawer.customer-details-drawer,
      :host ::ng-deep .p-drawer.sale-details-drawer {
        width: min(40rem, calc(100vw - 0.75rem)) !important;
        max-width: calc(100vw - 0.75rem) !important;
      }
      :host ::ng-deep .p-drawer.card-detail-drawer {
        width: min(64rem, calc(100vw - 0.75rem)) !important;
        max-width: calc(100vw - 0.75rem) !important;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-content,
      :host ::ng-deep .sale-details-drawer .p-drawer-content,
      :host ::ng-deep .card-detail-drawer .p-drawer-content {
        overflow-x: hidden;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-content p,
      :host ::ng-deep .customer-details-drawer .p-drawer-content a,
      :host ::ng-deep .customer-details-drawer .p-drawer-content span,
      :host ::ng-deep .sale-details-drawer .p-drawer-content p,
      :host ::ng-deep .sale-details-drawer .p-drawer-content a,
      :host ::ng-deep .sale-details-drawer .p-drawer-content span {
        overflow-wrap: anywhere;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .card-detail-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .card-detail-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .card-detail-table .p-datatable-table {
        table-layout: fixed;
        width: 100%;
      }
      :host ::ng-deep .card-detail-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.625rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        padding: 0.3rem 0.35rem;
        white-space: nowrap;
      }
      :host ::ng-deep .card-detail-table th.p-sortable-column {
        cursor: pointer;
        user-select: none;
      }
      :host ::ng-deep .card-detail-table .p-sortable-column-icon,
      :host ::ng-deep .card-detail-table .p-datatable-sort-icon,
      :host ::ng-deep .customer-cashflow-detail-dialog .p-sortable-column-icon,
      :host ::ng-deep .customer-cashflow-detail-dialog .p-datatable-sort-icon {
        font-size: 0.65rem;
        margin-left: 0.2rem;
      }
      :host ::ng-deep .card-detail-table .p-datatable-tbody > tr > td {
        font-size: 0.7rem;
        line-height: 1.25;
        padding: 0.28rem 0.35rem;
        overflow: hidden;
      }
      :host ::ng-deep .card-detail-table .p-datatable-tbody > tr > td:first-child {
        overflow: visible;
      }
      :host ::ng-deep .card-detail-drawer .p-paginator {
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.25rem;
      }
      :host ::ng-deep .card-detail-drawer .p-paginator .p-paginator-current {
        font-size: 0.7rem;
      }
      :host ::ng-deep .card-detail-table .p-paginator {
        font-size: 0.75rem;
      }
      :host ::ng-deep .customer-cashflow-detail-dialog .p-datatable-thead > tr > th {
        font-size: 0.6875rem;
        font-weight: 600;
        padding: 0.4rem 0.6rem;
      }
      :host ::ng-deep .customer-cashflow-detail-dialog .p-datatable-tbody > tr > td {
        font-size: 0.75rem;
        padding: 0.35rem 0.6rem;
      }
      :host ::ng-deep .top-customers-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        font-size: 0.6875rem;
        font-weight: 600;
        line-height: 1.2;
        padding: 0.3rem 0.45rem;
        white-space: nowrap;
      }
      :host ::ng-deep .top-customers-table .p-datatable-tbody > tr > td {
        font-size: 0.75rem;
        line-height: 1.2;
        padding: 0.22rem 0.45rem;
      }
      :host ::ng-deep .top-customers-table .p-datatable-table {
        min-width: 0;
      }
    `
  ]
})
export class ForecastCashFlowV2Component implements OnInit {
  private forecastService = inject(ForecastCashFlowV2Service);
  private customerService = inject(CustomerService);
  private saleService = inject(SaleService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  private readonly currentYear = browserCalendarYear();

  readonly help = {
    currentMonthIncome:
      'Suma de cargos cobrados (pagos aplicados) en el mes calendario actual.\n\n' +
      'Solo incluye Charge con status PAID cuya fecha de pago (paidAt) cae en este mes.\n\n' +
      'No es proyección: son ingresos realmente aplicados.\n\n' +
      'Haz clic en la card para ver el desglose de esos pagos.',

    pending:
      'Recibos sin pagar del mes calendario actual.\n\n' +
      'Monto y cantidad de cargos abiertos cuya dueDate cae en este mes.\n\n' +
      'Excluye los que vencieron en otro mes.\n\n' +
      'Haz clic en la card para ver el desglose de esos recibos.',

    overdue:
      'Todos los cargos vencidos sin pagar.\n\n' +
      'Incluye recibos con dueDate anterior a hoy, del mes actual o de meses anteriores.\n\n' +
      'No incluye cancelados, expirados ni pagados.\n\n' +
      'Haz clic en la card para ver el desglose de esos recibos.',

    nextMonth:
      'Ingresos esperados de un mes futuro (proyección).\n\n' +
      'Suma de cargos no cancelados, no expirados y no pagados cuya fecha de vencimiento (dueDate) cae en ese mes, ' +
      'más renovaciones proyectadas de suscripciones activas o en prueba.\n\n' +
      'Si una suscripción ya tiene un cargo materializado para esa fecha, no se duplica.\n\n' +
      'Usa el selector o las flechas para ver otros meses futuros.\n' +
      'Haz clic en el monto para ver el desglose.',

    cashflow:
      'Comparación mes a mes de dos años completos.\n\n' +
      'Cada barra es la suma de pagos cobrados (status PAID) de ese mes, según la fecha de pago del pago (Payment.paidAt).\n' +
      'El monto es el subtotal del cargo, sin IVA. El número al pie es cuántos pagos entran.\n' +
      'No incluye pagos cancelados, fallidos, pendientes ni renovaciones proyectadas.\n\n' +
      'Haz clic en una barra para ver los pagos de ese mes y año.',

    product:
      'Desglose por producto del mes y año seleccionados.\n\n' +
      'Solo cargos cobrados (status PAID) cuya fecha de pago cae en ese mes (subtotal, sin impuestos).\n' +
      'No incluye renovaciones proyectadas de suscripciones.\n\n' +
      'El selector llega hasta el mes actual.\n' +
      'Haz clic en un producto (lista o gráfica) para ver el desglose.',

    payment:
      'Desglose por forma de pago del mes y año seleccionados.\n\n' +
      'Solo cargos cobrados (status PAID) cuya fecha de pago cae en ese mes (subtotal, sin impuestos).\n' +
      'No incluye renovaciones proyectadas de suscripciones.\n\n' +
      'El selector llega hasta el mes actual.\n' +
      'Haz clic en una forma de pago (lista o gráfica) para ver el desglose.',

    monthly:
      'Top clientes por cobrado.\n\n' +
      'Se ordenan por el año más reciente ≤ actual entre A y B (si el otro es futuro, usa el actual).\n\n' +
      'Clic en el cliente para ver su ficha.\n' +
      'Clic en Cobrado A o Cobrado B para ver el desglose de pagos de ese año.'
  } as const;

  readonly glossary: { term: string; definition: string }[] = [
    {
      term: 'Año A / Año B',
      definition:
        'Dos años calendario completos (ene–dic) para comparar en Ingresos de caja y Top clientes. No cambian las cards del mes actual ni la proyección. Por defecto, B es el año en curso y A el anterior.'
    },
    {
      term: 'Ingresos del mes',
      definition:
        'Suma de cargos cobrados en el mes calendario en curso (fecha de pago), en subtotal sin impuestos. No depende de año A/B. Clic en la card para ver esos pagos.'
    },
    {
      term: 'Pagos pendientes del mes',
      definition:
        'Recibos sin pagar cuya fecha de vencimiento cae en el mes calendario actual (incluye los que ya vencieron este mes). Subtotal sin impuestos. No depende de año A/B. Clic en la card para ver el desglose.'
    },
    {
      term: 'Pagos vencidos',
      definition:
        'Todos los cargos abiertos con fecha de vencimiento anterior a hoy, del mes actual o de meses anteriores. No incluye cancelados, expirados ni pagados. No depende de año A/B. Clic en la card para ver el desglose.'
    },
    {
      term: 'Proyección',
      definition:
        'Cobro estimado de un mes futuro (desde el siguiente, hasta 12 meses). Cargos no cancelados, no expirados y no pagados que vencen en ese mes, más renovaciones proyectadas de suscripciones activas o en prueba. Subtotal sin impuestos. Clic en el monto para ver el desglose.'
    },
    {
      term: 'Ingresos de caja',
      definition:
        'Comparación mes a mes de año A vs año B. Cada barra suma el subtotal sin IVA de pagos cobrados (status PAID) en ese mes, según Payment.paidAt. El número al pie es cuántos pagos entran. Clic en la barra para verlos. No incluye pagos cancelados ni renovaciones proyectadas.'
    },
    {
      term: 'Por producto',
      definition:
        'Desglose del mes y año elegidos (selector propio, hasta el mes actual): solo cargos cobrados (fecha de pago), subtotal sin impuestos, sin proyección. Clic en un producto (lista o gráfica) para ver los pagos.'
    },
    {
      term: 'Por forma de pago',
      definition:
        'Mismo criterio que Por producto, agrupado por forma de pago del cargo cobrado. Clic en una forma de pago (lista o gráfica) para ver los pagos.'
    },
    {
      term: 'Top clientes',
      definition:
        'Ranking por cobrado (cargos pagados, fecha de pago, subtotal sin impuestos) del año más reciente ≤ actual entre A y B. Contrasta Cobrado A vs Cobrado B. Clic en el cliente para su ficha; en Cobrado A/B para el desglose de ese año.'
    }
  ];

  data = signal<ForecastCashFlowV2Data | null>(null);
  yearA = signal(this.currentYear - 1);
  yearB = signal(this.currentYear);
  /** Año del panel Por producto. */
  productYear = signal(browserCalendarYear());
  /** Mes 1–12 para el panel Por producto. */
  productMonth = signal(browserCalendarMonth());
  private productPeriodTouched = false;

  /** Año del panel Por forma de pago. */
  paymentYear = signal(browserCalendarYear());
  /** Mes 1–12 para el panel Por forma de pago. */
  paymentMonth = signal(browserCalendarMonth());
  private paymentPeriodTouched = false;

  /** Año/mes de la card de proyección (por defecto, el mes siguiente). */
  projectionYear = signal(this.nextCalendarPeriod().year);
  projectionMonth = signal(this.nextCalendarPeriod().month);
  projectionBusy = signal(false);
  private projectionLoadSeq = 0;
  private projectionSummary = signal<{ year: number; month: number; total: number; count: number } | null>(
    null
  );
  private projectionCache = signal<ForecastSnapshotCardDetail | null>(null);
  private readonly projectionHorizonMonths = 12;

  /** Cuántos productos mostrar en la leyenda (paginación local). */
  private readonly productLegendPageSize = 12;
  productLegendLimit = signal(10);

  /** Top clientes: página de 8, con “cargar más”. */
  private readonly topCustomersPageSize = 8;
  topCustomersLimit = signal(8);
  glossaryOpen = signal(false);

  /** Desglose de cobrado al hacer clic en Cobrado A / Cobrado B. */
  customerDetailOpen = signal(false);
  customerDetail = signal<ForecastCustomerCashFlowDetail | null>(null);
  customerDetailYearTab = signal<'A' | 'B'>('B');
  /** Claves expandidas de ventas en el diálogo (dataKey = key). */
  expandedSaleKeys = signal<Record<string, boolean>>({});

  /** Drawer de ficha del cliente (customer-read). */
  isCustomerDetailsOpen = signal(false);
  selectedCustomer = signal<CustomerDTO | null>(null);
  customerDetailsLoading = signal(false);

  /** Drawer de detalle de venta (sale-read). */
  isSaleDetailsOpen = signal(false);
  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  saleDetailsLoading = signal(false);

  /** Drawer de desglose de una card del header. */
  isCardDetailOpen = signal(false);
  cardDetail = signal<ForecastSnapshotCardDetail | null>(null);
  readonly cardDetailPageSize = 20;

  readonly cardDetailDateHeader = computed(() => {
    const kind = this.cardDetail()?.dateKind;
    if (kind === 'PAID_AT') return 'Fecha de pago';
    if (kind === 'EVENT_DATE') return 'Fecha';
    return 'Vencimiento';
  });

  readonly cardDetailDateSortField = computed(() =>
    this.cardDetail()?.dateKind === 'PAID_AT' ? 'paidAt' : 'dueDate'
  );

  /** Offset del paginador del drawer de desglose; al ordenar vuelve a 0. */
  cardDetailTableFirst = signal(0);

  /** Mismo permiso que el reporte de cobranza (CSV). */
  readonly canExportCharges = this.authService.hasRole('CHARGE_EXPORT');
  exporting = signal(false);

  readonly cardExportTooltip = computed(() => {
    if (!this.canExportCharges) {
      return 'No tienes permiso para descargar este listado. Pide acceso a un administrador.';
    }
    const detail = this.cardDetail();
    if (!detail || detail.loading || detail.items.length === 0) {
      return 'No hay pagos para exportar';
    }
    return 'Descargar desglose en CSV';
  });

  readonly customerDetailExportTooltip = computed(() => {
    if (!this.canExportCharges) {
      return 'No tienes permiso para descargar este listado. Pide acceso a un administrador.';
    }
    const sales = this.customerDetailSales();
    if (sales.length === 0) {
      return 'No hay pagos para exportar';
    }
    return 'Descargar desglose en CSV';
  });

  /** Opciones de año (últimos 8 + próximo). */
  readonly yearOptions = computed(() => {
    const end = this.currentYear + 1;
    const start = end - 8;
    const years: number[] = [];
    for (let y = end; y >= start; y--) {
      years.push(y);
    }
    return years;
  });

  readonly periodLabel = computed(() => `${this.yearA()} vs ${this.yearB()}`);

  readonly cashflowChart = computed(() => {
    const months = this.data()?.months ?? [];
    const a = this.data()?.yearA ?? this.yearA();
    const b = this.data()?.yearB ?? this.yearB();
    return {
      labels: months.map((m) => m.label),
      datasets: [
        {
          type: 'bar' as const,
          label: String(a),
          backgroundColor: 'rgba(30, 58, 95, 0.92)',
          hoverBackgroundColor: 'rgba(59, 91, 140, 1)',
          borderColor: 'transparent',
          hoverBorderColor: 'rgba(15, 23, 42, 0.45)',
          borderWidth: 0,
          hoverBorderWidth: 2,
          data: months.map((m) => m.yearACollected),
          saleCounts: months.map((m) => m.yearACollectedCount),
          borderRadius: 4,
          order: 2
        },
        {
          type: 'bar' as const,
          label: String(b),
          backgroundColor: 'rgba(16, 185, 129, 0.9)',
          hoverBackgroundColor: 'rgba(52, 211, 153, 1)',
          borderColor: 'transparent',
          hoverBorderColor: 'rgba(6, 95, 70, 0.55)',
          borderWidth: 0,
          hoverBorderWidth: 2,
          data: months.map((m) => m.yearBCollected),
          saleCounts: months.map((m) => m.yearBCollectedCount),
          borderRadius: 4,
          order: 2
        }
      ]
    };
  });

  readonly cashflowPlugins = [cashflowBarSaleCountPlugin];

  readonly cashflowOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'nearest' as const, intersect: true },
    layout: {
      padding: { top: 12, right: 8, bottom: 4, left: 4 }
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { boxWidth: 10, font: { size: 10 }, padding: 8 }
      },
      tooltip: {
        // Evita que el tooltip se dibuje fuera del canvas y se recorte
        mode: 'index' as const,
        intersect: false,
        yAlign: 'bottom' as const,
        padding: 10,
        callbacks: {
          label: (ctx: {
            dataset: { label?: string; saleCounts?: number[] };
            dataIndex: number;
            parsed: { y: number | null };
          }) => {
            const v = ctx.parsed.y ?? 0;
            const n = ctx.dataset.saleCounts?.[ctx.dataIndex] ?? 0;
            const pagos = n === 1 ? '1 pago' : `${n} pagos`;
            return `${ctx.dataset.label}: ${this.formatMxn(v)} · ${pagos}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: false,
        grid: { display: false },
        ticks: { color: '#64748b', font: { size: 10 }, maxRotation: 0 }
      },
      y: {
        stacked: false,
        beginAtZero: true,
        grace: '8%',
        grid: { color: 'rgba(15, 23, 42, 0.06)' },
        ticks: {
          color: '#64748b',
          font: { size: 10 },
          callback: (value: string | number) => this.formatMxn(Number(value))
        }
      }
    },
    onClick: (
      event: unknown,
      elements: Array<{ index?: number; datasetIndex?: number }>,
      chart?: {
        getElementsAtEventForMode?: (
          ev: unknown,
          mode: string,
          options: { intersect: boolean },
          useFinalPosition: boolean
        ) => Array<{ index: number; datasetIndex: number }>;
      }
    ) => {
      const hits = chart?.getElementsAtEventForMode?.(event, 'nearest', { intersect: true }, true);
      const hit = hits?.[0] ?? elements?.[0];
      if (hit?.index != null && hit.datasetIndex != null) {
        this.onCashflowBarSelect(hit.datasetIndex, hit.index);
        return;
      }
      const labelHit = findSaleCountHit(chart, event);
      if (labelHit) this.onCashflowBarSelect(labelHit.datasetIndex, labelHit.index);
    },
    onHover: (
      event: { native?: { target?: HTMLElement }; x?: number; y?: number },
      elements: unknown[],
      chart?: object
    ) => {
      const target = event?.native?.target;
      if (!target?.style) return;
      const overLabel = !!findSaleCountHit(chart, event);
      target.style.cursor = elements?.length || overLabel ? 'pointer' : 'default';
    }
  };

  private readonly doughnutColors = [
    'rgba(14, 165, 233, 0.9)',
    'rgba(16, 185, 129, 0.9)',
    'rgba(245, 158, 11, 0.9)',
    'rgba(99, 102, 241, 0.9)',
    'rgba(244, 63, 94, 0.85)',
    'rgba(15, 118, 110, 0.85)',
    'rgba(168, 85, 247, 0.85)'
  ];

  readonly productMonthOptions = [
    { value: 1, label: 'ene' },
    { value: 2, label: 'feb' },
    { value: 3, label: 'mar' },
    { value: 4, label: 'abr' },
    { value: 5, label: 'may' },
    { value: 6, label: 'jun' },
    { value: 7, label: 'jul' },
    { value: 8, label: 'ago' },
    { value: 9, label: 'sep' },
    { value: 10, label: 'oct' },
    { value: 11, label: 'nov' },
    { value: 12, label: 'dic' }
  ];

  /** Periodos mes+año disponibles: años de la comparación, hasta el mes calendario actual. */
  readonly productPeriodOptions = computed(() => {
    const years = this.productYearOptions().slice().sort((a, b) => a - b);
    const currentYear = browserCalendarYear();
    const currentMonth = browserCalendarMonth();
    const opts: { value: string; label: string; year: number; month: number }[] = [];
    for (const year of years) {
      if (year > currentYear) continue;
      for (const m of this.productMonthOptions) {
        if (year === currentYear && m.value > currentMonth) continue;
        opts.push({
          value: `${year}-${String(m.value).padStart(2, '0')}`,
          label: `${m.label} ${year}`,
          year,
          month: m.value
        });
      }
    }
    return opts;
  });

  readonly productYearOptions = computed(() => {
    const d = this.data();
    const years = new Set<number>([
      this.yearA(),
      this.yearB(),
      this.currentYear,
      this.productYear(),
      this.paymentYear()
    ]);
    if (d?.yearA) years.add(d.yearA);
    if (d?.yearB) years.add(d.yearB);
    return [...years].sort((a, b) => b - a);
  });

  readonly productPeriodValue = computed(() => {
    const y = this.productYear();
    const m = String(this.productMonth()).padStart(2, '0');
    return `${y}-${m}`;
  });

  readonly paymentPeriodValue = computed(() => {
    const y = this.paymentYear();
    const m = String(this.paymentMonth()).padStart(2, '0');
    return `${y}-${m}`;
  });

  readonly canPrevProductPeriod = computed(() => this.canShiftPaidPeriod(-1, 'product'));
  readonly canNextProductPeriod = computed(() => this.canShiftPaidPeriod(1, 'product'));
  readonly canPrevPaymentPeriod = computed(() => this.canShiftPaidPeriod(-1, 'payment'));
  readonly canNextPaymentPeriod = computed(() => this.canShiftPaidPeriod(1, 'payment'));

  /** 12 meses futuros a partir del mes calendario siguiente. */
  readonly projectionPeriodOptions = computed(() => {
    const start = this.nextCalendarPeriod();
    const opts: { value: string; label: string; year: number; month: number }[] = [];
    for (let i = 0; i < this.projectionHorizonMonths; i++) {
      const p = ForecastCashFlowV2Component.shiftCalendarPeriod(start.year, start.month, i);
      const monthOpt = this.productMonthOptions.find((m) => m.value === p.month);
      opts.push({
        value: `${p.year}-${String(p.month).padStart(2, '0')}`,
        label: `${monthOpt?.label ?? p.month} ${p.year}`,
        year: p.year,
        month: p.month
      });
    }
    return opts;
  });

  readonly projectionPeriodValue = computed(() => {
    const y = this.projectionYear();
    const m = String(this.projectionMonth()).padStart(2, '0');
    return `${y}-${m}`;
  });

  readonly canPrevProjectionPeriod = computed(() => {
    const idx = this.projectionPeriodOptions().findIndex((o) => o.value === this.projectionPeriodValue());
    return idx > 0;
  });

  readonly canNextProjectionPeriod = computed(() => {
    const opts = this.projectionPeriodOptions();
    const idx = opts.findIndex((o) => o.value === this.projectionPeriodValue());
    return idx >= 0 && idx < opts.length - 1;
  });

  readonly projectionTotal = computed(() => {
    const summary = this.projectionSummary();
    const y = this.projectionYear();
    const m = this.projectionMonth();
    if (summary && summary.year === y && summary.month === m) {
      return summary.total;
    }
    if (this.isDefaultProjectionPeriod()) {
      return this.data()?.cards?.nextMonthProjection ?? 0;
    }
    return 0;
  });

  readonly projectionCount = computed(() => {
    const summary = this.projectionSummary();
    const y = this.projectionYear();
    const m = this.projectionMonth();
    if (summary && summary.year === y && summary.month === m) {
      return summary.count;
    }
    if (this.isDefaultProjectionPeriod()) {
      return this.data()?.cards?.nextMonthEventCount ?? 0;
    }
    return 0;
  });

  readonly selectedProductMonth = computed(() => {
    const month = this.productMonth();
    const year = this.productYear();
    const rows = this.data()?.byProductByMonth ?? [];
    return rows.find((r) => r.monthOfYear === month && r.year === year) ?? null;
  });

  readonly selectedPaymentMonth = computed(() => {
    const month = this.paymentMonth();
    const year = this.paymentYear();
    const rows = this.data()?.byPaymentMethodByMonth ?? [];
    return rows.find((r) => r.monthOfYear === month && r.year === year) ?? null;
  });

  readonly productLegendAll = computed(() => {
    const products = this.selectedProductMonth()?.products ?? [];
    return products.map((r, i) => ({
      label: r.key || 'Sin producto',
      amount: r.amount,
      count: r.count,
      color: this.doughnutColors[i % this.doughnutColors.length]
    }));
  });

  readonly productLegend = computed(() =>
    this.productLegendAll().slice(0, this.productLegendLimit())
  );

  readonly productLegendHasMore = computed(
    () => this.productLegendLimit() < this.productLegendAll().length
  );

  readonly productLegendRemaining = computed(() =>
    Math.max(0, this.productLegendAll().length - this.productLegendLimit())
  );

  readonly paymentLegend = computed(() => {
    const rows = this.selectedPaymentMonth()?.products ?? [];
    return rows.map((r, i) => ({
      key: r.key || '—',
      label: this.paymentLabel(r.key),
      amount: r.amount,
      color: this.doughnutColors[i % this.doughnutColors.length]
    }));
  });

  readonly doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    layout: { padding: 8 },
    plugins: {
      legend: { display: false },
      tooltip: {
        yAlign: 'bottom' as const,
        padding: 8
      }
    }
  };

  readonly productDoughnutOptions = {
    ...this.doughnutOptions,
    onClick: (_event: unknown, elements: Array<{ index?: number }>) => {
      this.onProductChartSelect(elements);
    },
    onHover: (event: { native?: { target?: HTMLElement } }, elements: unknown[]) => {
      const target = event?.native?.target;
      if (target?.style) {
        target.style.cursor = elements?.length ? 'pointer' : 'default';
      }
    }
  };

  readonly paymentDoughnutOptions = {
    ...this.doughnutOptions,
    onClick: (_event: unknown, elements: Array<{ index?: number }>) => {
      this.onPaymentChartSelect(elements);
    },
    onHover: (event: { native?: { target?: HTMLElement } }, elements: unknown[]) => {
      const target = event?.native?.target;
      if (target?.style) {
        target.style.cursor = elements?.length ? 'pointer' : 'default';
      }
    }
  };

  readonly productChart = computed(() => {
    const products = this.selectedProductMonth()?.products ?? [];
    return {
      labels: products.map((r) => r.key || 'Sin producto'),
      datasets: [
        {
          data: products.map((r) => r.amount),
          backgroundColor: this.doughnutColors,
          borderWidth: 0,
          hoverOffset: 2
        }
      ]
    };
  });

  readonly paymentChart = computed(() => {
    const rows = this.selectedPaymentMonth()?.products ?? [];
    return {
      labels: rows.map((r) => this.paymentLabel(r.key)),
      datasets: [
        {
          data: rows.map((r) => r.amount),
          backgroundColor: this.doughnutColors,
          borderWidth: 0,
          hoverOffset: 2
        }
      ]
    };
  });

  readonly topCustomersAll = computed(() => this.data()?.topCustomers ?? []);

  readonly topCustomers = computed(() =>
    this.topCustomersAll().slice(0, this.topCustomersLimit())
  );

  readonly topCustomersHasMore = computed(
    () => this.topCustomersLimit() < this.topCustomersAll().length
  );

  readonly topCustomersRemaining = computed(() =>
    Math.max(0, this.topCustomersAll().length - this.topCustomersLimit())
  );

  readonly customerDetailSales = computed((): ForecastCustomerSaleBucket[] => {
    const d = this.customerDetail();
    if (!d) return [];
    return this.customerDetailYearTab() === 'B' ? d.yearBSales : d.yearASales;
  });

  readonly customerDetailYearTotal = computed(() => {
    const d = this.customerDetail();
    if (!d) return 0;
    return this.customerDetailYearTab() === 'B' ? d.yearBTotal : d.yearATotal;
  });

  readonly customerDetailYearLabel = computed(() => {
    const d = this.customerDetail();
    if (!d) return '';
    return String(this.customerDetailYearTab() === 'B' ? d.yearB : d.yearA);
  });

  ngOnInit(): void {
    this.load();
  }

  toggleGlossary(): void {
    this.glossaryOpen.update((v) => !v);
  }

  prevProductPeriod(): void {
    this.productPeriodTouched = true;
    this.shiftPeriod(-1, 'product');
  }

  nextProductPeriod(): void {
    this.productPeriodTouched = true;
    this.shiftPeriod(1, 'product');
  }

  prevPaymentPeriod(): void {
    this.paymentPeriodTouched = true;
    this.shiftPeriod(-1, 'payment');
  }

  nextPaymentPeriod(): void {
    this.paymentPeriodTouched = true;
    this.shiftPeriod(1, 'payment');
  }

  prevProjectionPeriod(): void {
    this.shiftProjectionPeriod(-1);
  }

  nextProjectionPeriod(): void {
    this.shiftProjectionPeriod(1);
  }

  onProjectionPeriodValueChange(value: string): void {
    this.applyProjectionPeriodFromValue(value);
  }

  private shiftProjectionPeriod(delta: number): void {
    const opts = this.projectionPeriodOptions();
    const idx = opts.findIndex((o) => o.value === this.projectionPeriodValue());
    const next = opts[idx + delta];
    if (!next) return;
    this.applyProjectionPeriod(next.year, next.month);
  }

  private applyProjectionPeriodFromValue(value: string): void {
    const match = /^(\d{4})-(\d{2})$/.exec(value);
    if (!match) return;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return;
    this.applyProjectionPeriod(year, month);
  }

  private applyProjectionPeriod(year: number, month: number): void {
    if (this.projectionYear() === year && this.projectionMonth() === month) return;
    this.projectionYear.set(year);
    this.projectionMonth.set(month);
    if (this.isDefaultProjectionPeriod()) {
      this.projectionLoadSeq += 1;
      this.projectionBusy.set(false);
      this.projectionSummary.set(null);
      this.projectionCache.set(null);
      return;
    }
    this.loadProjectionSummary();
  }

  private loadProjectionSummary(): void {
    const year = this.projectionYear();
    const month = this.projectionMonth();
    const seq = ++this.projectionLoadSeq;
    this.projectionBusy.set(true);
    this.forecastService.loadCardDetail('NEXT_MONTH_PROJECTION', { year, month }).subscribe((d) => {
      if (seq !== this.projectionLoadSeq) return;
      this.projectionBusy.set(false);
      this.projectionSummary.set({ year, month, total: d.total, count: d.count });
      if (!d.error) {
        this.projectionCache.set(d);
      } else {
        this.projectionCache.set(null);
      }
    });
  }

  private isDefaultProjectionPeriod(): boolean {
    const next = this.nextCalendarPeriod();
    return this.projectionYear() === next.year && this.projectionMonth() === next.month;
  }

  private nextCalendarPeriod(): { year: number; month: number } {
    return ForecastCashFlowV2Component.shiftCalendarPeriod(
      browserCalendarYear(),
      browserCalendarMonth(),
      1
    );
  }

  private static shiftCalendarPeriod(
    year: number,
    month: number,
    delta: number
  ): { year: number; month: number } {
    const idx = year * 12 + (month - 1) + delta;
    return { year: Math.floor(idx / 12), month: (idx % 12) + 1 };
  }

  private shiftPeriod(delta: number, which: 'product' | 'payment'): void {
    const value = which === 'product' ? this.productPeriodValue() : this.paymentPeriodValue();
    const opts = this.productPeriodOptions();
    const idx = opts.findIndex((o) => o.value === value);
    const next = opts[idx + delta];
    if (!next) return;
    this.applyPeriod(next.year, next.month, which);
  }

  private canShiftPaidPeriod(delta: number, which: 'product' | 'payment'): boolean {
    const value = which === 'product' ? this.productPeriodValue() : this.paymentPeriodValue();
    const opts = this.productPeriodOptions();
    const idx = opts.findIndex((o) => o.value === value);
    return idx >= 0 && idx + delta >= 0 && idx + delta < opts.length;
  }

  private applyPeriod(year: number, month: number, which: 'product' | 'payment'): void {
    const currentYear = browserCalendarYear();
    const currentMonth = browserCalendarMonth();
    if (year > currentYear || (year === currentYear && month > currentMonth)) {
      year = currentYear;
      month = currentMonth;
    }

    const options = this.productYearOptions();
    const setYear = which === 'product'
      ? (y: number) => this.productYear.set(y)
      : (y: number) => this.paymentYear.set(y);
    const setMonth = which === 'product'
      ? (m: number) => this.productMonth.set(m)
      : (m: number) => this.paymentMonth.set(m);

    if (which === 'product') {
      this.resetProductLegendLimit();
    }

    if (options.length === 0) {
      setYear(year);
      setMonth(month);
      return;
    }
    const minY = Math.min(...options);
    const maxY = Math.max(...options.filter((y) => y <= currentYear));
    if (year < minY) {
      setYear(minY);
      setMonth(1);
      return;
    }
    if (year > maxY) {
      setYear(maxY);
      setMonth(maxY === currentYear ? currentMonth : 12);
      return;
    }
    setYear(year);
    setMonth(month);
  }

  loadMoreProducts(): void {
    this.productLegendLimit.update((n) => n + this.productLegendPageSize);
  }

  private resetProductLegendLimit(): void {
    this.productLegendLimit.set(this.productLegendPageSize);
  }

  loadMoreTopCustomers(): void {
    this.topCustomersLimit.update((n) => n + this.topCustomersPageSize);
  }

  openCustomerDetails(customerId: number): void {
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: () => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('Error al cargar el cliente');
        this.closeCustomerDetails();
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }

  editCustomerFromDrawer(): void {
    const customer = this.selectedCustomer();
    if (!customer?.id) return;
    this.closeCustomerDetails();
    this.router.navigate(['/customers', customer.id]);
  }

  openSaleDetails(saleId: number | null | undefined, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (!saleId) return;
    this.isSaleDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          this.saleService.getSaleCharges(detail.id!).subscribe({
            next: (chargesResp) => this.saleCharges.set(chargesResp.body ?? []),
            error: () => this.saleCharges.set([])
          });
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle de la venta');
          this.closeSaleDetails();
        }
        this.saleDetailsLoading.set(false);
      },
      error: () => {
        this.saleDetailsLoading.set(false);
        this.toastr.error('Error al cargar el detalle de la venta');
        this.closeSaleDetails();
      }
    });
  }

  closeSaleDetails(): void {
    this.isSaleDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleDetails();
  }

  editSaleFromDrawer(): void {
    const sale = this.selectedSale();
    if (!sale?.id) return;
    this.closeSaleDetails();
    this.closeCustomerDetail();
    this.router.navigate(['/sales', sale.id]);
  }

  openSaleFromCustomer(saleId: number): void {
    this.openSaleDetails(saleId);
  }

  openCustomerFromSale(customerId: number): void {
    if (!customerId) return;
    this.openCustomerDetails(customerId);
  }

  openCardDetail(card: ForecastSnapshotCardKind): void {
    this.isCardDetailOpen.set(true);
    this.resetCardDetailPager();
    this.cardDetail.set({
      card,
      title: 'Desglose',
      subtitle: 'pagos',
      dateKind: card === 'CURRENT_MONTH_INCOME' ? 'PAID_AT' : 'DUE_DATE',
      total: 0,
      count: 0,
      items: [],
      loading: true
    });
    const projectionOpts =
      card === 'NEXT_MONTH_PROJECTION'
        ? { year: this.projectionYear(), month: this.projectionMonth() }
        : undefined;
    const cached = this.projectionCache();
    const summary = this.projectionSummary();
    if (
      card === 'NEXT_MONTH_PROJECTION' &&
      cached &&
      !cached.loading &&
      !cached.error &&
      summary &&
      summary.year === this.projectionYear() &&
      summary.month === this.projectionMonth()
    ) {
      this.cardDetail.set(cached);
      return;
    }
    this.forecastService.loadCardDetail(card, projectionOpts).subscribe((d) => {
      this.cardDetail.set(d);
      if (card === 'NEXT_MONTH_PROJECTION' && !d.loading && !d.error) {
        this.projectionCache.set(d);
        this.projectionSummary.set({
          year: this.projectionYear(),
          month: this.projectionMonth(),
          total: d.total,
          count: d.count
        });
      }
    });
  }

  onProductChartSelect(elements: Array<{ index?: number }> | undefined): void {
    const index = elements?.[0]?.index;
    if (index == null || index < 0) return;
    const product = this.selectedProductMonth()?.products?.[index];
    if (!product) return;
    this.openProductDetail(product.key || 'Sin producto');
  }

  openProductDetail(product: string): void {
    const key = (product || 'Sin producto').trim() || 'Sin producto';
    const year = this.productYear();
    const month = this.productMonth();
    this.isCardDetailOpen.set(true);
    this.resetCardDetailPager();
    this.cardDetail.set({
      card: 'PRODUCT',
      title: key,
      subtitle: 'pagos cobrados',
      dateKind: 'PAID_AT',
      total: 0,
      count: 0,
      items: [],
      loading: true
    });
    this.forecastService.loadProductDetail(year, month, key).subscribe((d) => this.cardDetail.set(d));
  }

  onPaymentChartSelect(elements: Array<{ index?: number }> | undefined): void {
    const index = elements?.[0]?.index;
    if (index == null || index < 0) return;
    const row = this.selectedPaymentMonth()?.products?.[index];
    if (!row) return;
    this.openPaymentDetail(row.key || '—');
  }

  openPaymentDetail(payment: string): void {
    const key = (payment || '—').trim() || '—';
    const year = this.paymentYear();
    const month = this.paymentMonth();
    this.isCardDetailOpen.set(true);
    this.resetCardDetailPager();
    this.cardDetail.set({
      card: 'PAYMENT',
      title: this.paymentLabel(key),
      subtitle: 'pagos cobrados',
      dateKind: 'PAID_AT',
      total: 0,
      count: 0,
      items: [],
      loading: true
    });
    this.forecastService.loadPaymentDetail(year, month, key).subscribe((d) => {
      const suffix = d.title.includes(' · ') ? d.title.slice(d.title.indexOf(' ·')) : '';
      this.cardDetail.set({ ...d, title: this.paymentLabel(key) + suffix });
    });
  }

  onCashflowBarSelect(datasetIndex: number, monthIndex: number): void {
    const month = monthIndex + 1;
    if (month < 1 || month > 12) return;
    const d = this.data();
    const year = datasetIndex === 0 ? (d?.yearA ?? this.yearA()) : (d?.yearB ?? this.yearB());
    this.openMonthDetail(year, month);
  }

  openMonthDetail(year: number, month: number): void {
    const monthOpt = this.productMonthOptions.find((m) => m.value === month);
    this.isCardDetailOpen.set(true);
    this.resetCardDetailPager();
    this.cardDetail.set({
      card: 'MONTH',
      title: `Ingresos · ${monthOpt?.label ?? month} ${year}`,
      subtitle: 'pagos cobrados',
      dateKind: 'PAID_AT',
      total: 0,
      count: 0,
      items: [],
      loading: true
    });
    this.forecastService.loadMonthDetail(year, month).subscribe((d) => this.cardDetail.set(d));
  }

  closeCardDetail(): void {
    this.isCardDetailOpen.set(false);
    this.cardDetail.set(null);
    this.resetCardDetailPager();
  }

  onCardDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCardDetail();
  }

  onCardDetailSort(): void {
    this.resetCardDetailPager();
  }

  onCardDetailPage(event: { first?: number }): void {
    this.cardDetailTableFirst.set(event.first ?? 0);
  }

  private resetCardDetailPager(): void {
    this.cardDetailTableFirst.set(0);
  }

  cardChargeStatusLabel(status: string | null | undefined): string {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'DUE':
        return 'Vencido';
      case 'SCHEDULED':
        return 'Programado';
      case 'PAID':
        return 'Pagado';
      case 'FAILED':
        return 'Fallido';
      case 'EXPIRED':
        return 'Expirado';
      case 'CANCELLED':
        return 'Cancelado';
      case 'PROJECTED':
        return 'Proyectado';
      case 'PARTIALLY_PAID':
        return 'Parcial';
      default:
        return status || '—';
    }
  }

  cardChargeStatusSeverity(
    status: string | null | undefined
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'PAID') return 'success';
    if (status === 'PARTIALLY_PAID') return 'warn';
    if (status === 'PENDING' || status === 'SCHEDULED' || status === 'PROJECTED') return 'info';
    if (status === 'DUE') return 'warn';
    if (status === 'CANCELLED' || status === 'FAILED') return 'danger';
    return 'secondary';
  }

  exportCardDetailCsv(): void {
    const detail = this.cardDetail();
    if (!this.canExportCharges || this.exporting() || !detail || detail.loading || detail.items.length === 0) {
      return;
    }
    const dateHeader = this.cardDetailDateHeader();
    const header = ['#', 'Cliente', 'Venta', 'Notas', 'Pago', dateHeader, 'Estado', 'Origen', 'Monto'];
    const rows = detail.items.map((row) => [
      String(row.index),
      row.customerName,
      row.saleNumber || (row.saleId != null ? `#${row.saleId}` : ''),
      row.description,
      this.paymentFraction(row.paymentNumber, row.numberOfPayments),
      detail.dateKind === 'PAID_AT' ? this.formatCsvDateTime(row.paidAt) : this.formatCsvDate(row.dueDate),
      this.cardChargeStatusLabel(row.status),
      row.source === 'SUBSCRIPTION' ? 'Suscripción' : row.source === 'SALE' ? 'Venta' : row.source === 'PAYMENT' ? 'Pago' : 'Cargo',
      this.formatCsvAmount(row.total)
    ]);
    this.downloadCsv(header, rows, this.csvFilename(detail.title));
  }

  exportCustomerDetailCsv(): void {
    const detail = this.customerDetail();
    const sales = this.customerDetailSales();
    if (!this.canExportCharges || this.exporting() || !detail || detail.loading || sales.length === 0) {
      return;
    }
    const header = ['#', 'Cliente', 'Venta', 'Tipo', 'Descripción', 'Pago', 'Fecha de pago', 'Monto'];
    const rows: string[][] = [];
    let index = 0;
    for (const sale of sales) {
      const charges = sale.charges?.length ? sale.charges : [null];
      for (const ch of charges) {
        index += 1;
        rows.push([
          String(index),
          detail.customerName,
          sale.saleNumber || (sale.saleId != null ? `#${sale.saleId}` : ''),
          this.saleTypeLabel(sale.saleType),
          ch?.description || sale.description,
          this.paymentFraction(ch?.paymentNumber ?? null, ch?.numberOfPayments ?? null),
          this.formatCsvDateTime(ch?.paidAt ?? sale.lastPaidAt),
          this.formatCsvAmount(ch?.total ?? sale.amount)
        ]);
      }
    }
    const year = this.customerDetailYearLabel();
    this.downloadCsv(header, rows, this.csvFilename(`cobrado-${detail.customerName}-${year}`));
  }

  private paymentFraction(
    paymentNumber: number | null | undefined,
    numberOfPayments: number | null | undefined
  ): string {
    if (paymentNumber == null || numberOfPayments == null) return '';
    return `${paymentNumber}/${numberOfPayments}`;
  }

  private formatCsvDateTime(value: string | null | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('es-MX');
  }

  private formatCsvDate(value: string | null | undefined): string {
    if (!value) return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    return this.formatCsvDateTime(value);
  }

  private formatCsvAmount(value: number | null | undefined): string {
    return new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  }

  private csvFilename(title: string): string {
    const slug = title
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${slug || 'desglose'}.csv`;
  }

  private downloadCsv(header: string[], rows: string[][], filename: string): void {
    this.exporting.set(true);
    try {
      const lines = [header, ...rows].map((cols) => cols.map((c) => this.csvCell(c)).join(','));
      const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      this.toastr.success('CSV descargado');
    } catch {
      this.toastr.error('No se pudo descargar el CSV');
    } finally {
      this.exporting.set(false);
    }
  }

  private csvCell(value: string | number | null | undefined): string {
    const s = value == null ? '' : String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }

  openCustomerCollectedDetail(row: ForecastCustomerCompareBucket, year: 'A' | 'B'): void {
    if (!row?.customerId) return;
    const yearA = this.data()?.yearA ?? this.yearA();
    const yearB = this.data()?.yearB ?? this.yearB();
    this.customerDetailYearTab.set(year);
    this.expandedSaleKeys.set({});
    this.customerDetailOpen.set(true);
    this.customerDetail.set({
      customerId: row.customerId,
      customerName: row.customerName,
      yearA,
      yearB,
      yearATotal: 0,
      yearBTotal: 0,
      yearAChargeCount: 0,
      yearBChargeCount: 0,
      yearASales: [],
      yearBSales: [],
      loading: true
    });
    this.forecastService.loadCustomerDetail(row.customerId, yearA, yearB).subscribe((d) => {
      this.customerDetail.set({
        ...d,
        customerName: d.customerName || row.customerName
      });
    });
  }

  closeCustomerDetail(): void {
    this.customerDetailOpen.set(false);
    this.customerDetail.set(null);
    this.expandedSaleKeys.set({});
  }

  onCustomerDetailVisible(visible: boolean): void {
    if (!visible) {
      this.closeCustomerDetail();
    }
  }

  setCustomerDetailYearTab(tab: 'A' | 'B'): void {
    this.customerDetailYearTab.set(tab);
    this.expandedSaleKeys.set({});
  }

  toggleSaleExpand(sale: ForecastCustomerSaleBucket, event: Event): void {
    event.stopPropagation();
    const key = sale.key;
    this.expandedSaleKeys.update((keys) => ({
      ...keys,
      [key]: !keys[key]
    }));
  }

  saleTypeLabel(saleType: string | null | undefined): string {
    if (saleType === 'SUBSCRIPTION') return 'Suscripción';
    if (saleType === 'ONE_TIME') return 'Única';
    return 'Sin venta';
  }

  private resetTopCustomersLimit(): void {
    this.topCustomersLimit.set(this.topCustomersPageSize);
  }

  onProductPeriodChange(event: Event): void {
    this.productPeriodTouched = true;
    this.onPeriodSelectChange(event, 'product');
  }

  onPaymentPeriodChange(event: Event): void {
    this.paymentPeriodTouched = true;
    this.onPeriodSelectChange(event, 'payment');
  }

  onProductPeriodValueChange(value: string): void {
    this.productPeriodTouched = true;
    this.applyPeriodFromValue(value, 'product');
  }

  onPaymentPeriodValueChange(value: string): void {
    this.paymentPeriodTouched = true;
    this.applyPeriodFromValue(value, 'payment');
  }

  private applyPeriodFromValue(value: string, which: 'product' | 'payment'): void {
    const match = /^(\d{4})-(\d{2})$/.exec(value);
    if (!match) return;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return;
    this.applyPeriod(year, month, which);
  }

  private onPeriodSelectChange(event: Event, which: 'product' | 'payment'): void {
    const value = (event.target as HTMLSelectElement).value;
    this.applyPeriodFromValue(value, which);
  }

  setYearA(year: number): void {
    if (year >= this.yearB()) return;
    this.yearA.set(year);
    this.load();
  }

  setYearB(year: number): void {
    if (year <= this.yearA()) return;
    this.yearB.set(year);
    this.load();
  }

  onYearAChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (!Number.isFinite(value)) return;
    this.setYearA(value);
  }

  onYearBChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (!Number.isFinite(value)) return;
    this.setYearB(value);
  }

  load(): void {
    const yearA = this.yearA();
    const yearB = this.yearB();
    if (yearB <= yearA) return;
    this.data.update((d) =>
      d
        ? { ...d, loading: true, error: undefined }
        : {
            yearA,
            yearB,
            cards: {
              currentMonthLabel: '',
              nextMonthLabel: '',
              currentMonthIncome: 0,
              currentMonthExpected: 0,
              currentMonthEventCount: 0,
              currentMonthPending: 0,
              currentMonthPendingCount: 0,
              currentMonthOverdue: 0,
              currentMonthOverdueCount: 0,
              nextMonthProjection: 0,
              nextMonthCharges: 0,
              nextMonthSubscriptions: 0,
              nextMonthEventCount: 0
            },
            kpis: {
              totalProjected: 0,
              totalExpected: 0,
              chargesProjected: 0,
              subscriptionsProjected: 0,
              mrr: 0,
              atRiskAmount: 0,
              overdueAmount: 0,
              collectedInHorizon: 0,
              activeSubscriptions: 0,
              eventCount: 0
            },
            months: [],
            byCollectionMethod: [],
            byPaymentMethod: [],
            byProduct: [],
            byProductByMonth: [],
            byPaymentMethodByMonth: [],
            topCustomers: [],
            loading: true
          }
    );
    this.forecastService.loadForecast(yearA, yearB).subscribe((d) => {
      this.data.set(d);
      this.resetTopCustomersLimit();
      this.syncPeriodDefault(d, 'product');
      this.syncPeriodDefault(d, 'payment');
    });
  }

  /**
   * Al abrir/recargar (sin interacción del usuario): siempre mes y año actuales.
   * Si el usuario ya cambió el filtro, solo se corrige si el año elegido ya no es válido.
   */
  private syncPeriodDefault(
    _d: ForecastCashFlowV2Data,
    which: 'product' | 'payment'
  ): void {
    const currentYear = browserCalendarYear();
    const currentMonth = browserCalendarMonth();
    const touched = which === 'product' ? this.productPeriodTouched : this.paymentPeriodTouched;
    const selectedYear = which === 'product' ? this.productYear() : this.paymentYear();
    const setYear = which === 'product'
      ? (y: number) => this.productYear.set(y)
      : (y: number) => this.paymentYear.set(y);
    const setMonth = which === 'product'
      ? (m: number) => this.productMonth.set(m)
      : (m: number) => this.paymentMonth.set(m);

    if (!touched) {
      setYear(currentYear);
      setMonth(currentMonth);
      if (which === 'product') {
        this.resetProductLegendLimit();
      }
      return;
    }

    const allowed = this.productYearOptions();
    const selectedMonth = which === 'product' ? this.productMonth() : this.paymentMonth();
    const afterCurrent =
      selectedYear > currentYear || (selectedYear === currentYear && selectedMonth > currentMonth);
    if (!allowed.includes(selectedYear) || afterCurrent) {
      setYear(currentYear);
      setMonth(currentMonth);
      if (which === 'product') {
        this.resetProductLegendLimit();
      }
    }
  }

  formatMxn(amount: number): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  }

  paymentLabel(key: string): string {
    const map: Record<string, string> = {
      LINK: 'Liga de pago',
      TRANSFER: 'Transferencia',
      MANUAL: 'Manual',
      RECURRING_CHARGE: 'Cargo recurrente',
      '—': 'Sin forma de pago'
    };
    return map[key] || key || 'Sin forma de pago';
  }

  diffClass(amount: number): string {
    if (amount > 0) return 'text-emerald-700';
    if (amount < 0) return 'text-rose-700';
    return 'text-slate-600';
  }

  formatPercent(value: number): string {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  }
}
